/**
 * CustomEtapaConclusao.tsx — o formulário de FECHAR uma etapa, usado nos dois
 * lugares em que se fecha uma: dentro do card, na lista, e na página da etapa.
 *
 * Vive sozinho porque a primeira versão morava só na lista, e a página da
 * etapa ficou com um botão "Concluir na lista" que mandava a pessoa embora
 * para fazer ali o que devia poder fazer aqui — bengala, não desenho.
 *
 * ─── "NÃO PRECISOU" NÃO É "FEITO" ───────────────────────────────────────────
 * Os dois fecham a etapa, e por isso os dois botões estão lado a lado; mas
 * quem lê a ficha meses depois precisa saber se o abatimento aconteceu ou se
 * deixou de fazer sentido. Daí `feita` e `dispensada` serem estados distintos.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import type { UsuarioOpcao } from './shared';

interface Props {
  etapaId: number;
  usuarios: UsuarioOpcao[];
  /** Chamado depois de gravar — quem chamou recarrega o que precisa. */
  aoFechar: (gravou: boolean) => void;
}

export function CustomEtapaConclusao({ etapaId, usuarios, aoFechar }: Props) {
  const [obs, setObs] = useState('');
  const [proxima, setProxima] = useState('');
  const [proxResp, setProxResp] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function concluir(estado: 'feita' | 'dispensada') {
    setSalvando(true);
    try {
      await api.post(`/autorizacoes/etapas/${etapaId}/conclusao`, {
        estado,
        observacao: obs.trim() || null,
        // O gesto da Suany: fechar a minha parte já emendando a da Patrícia.
        proxima: proxima.trim()
          ? { titulo: proxima.trim(), responsavel_id: proxResp ? Number(proxResp) : null, depende_de: [] }
          : null,
      });
      aoFechar(true);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para concluir.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="casaa-etapa-concluir">
      <h6 className="mb-1">Concluir esta etapa</h6>
      <p className="small text-secondary mb-3">
        O que ficar escrito aqui é o que a Adriana lê para saber que fechou —
        de quem foi descontado, em qual pedido, quanto.
      </p>
      <div className="row g-2">
        <div className="col-12">
          <div className="form-floating">
            <textarea id={`etapa-obs-${etapaId}`} className="form-control" style={{ height: 90 }}
                      placeholder=" " value={obs} onChange={(ev) => setObs(ev.target.value)} />
            <label htmlFor={`etapa-obs-${etapaId}`}>Como ficou</label>
          </div>
        </div>
        <div className="col-sm-7">
          <div className="form-floating">
            <input id={`etapa-proxima-${etapaId}`} className="form-control" placeholder=" "
                   value={proxima} onChange={(ev) => setProxima(ev.target.value)} maxLength={200} />
            <label htmlFor={`etapa-proxima-${etapaId}`}>E o que vem depois? (opcional)</label>
          </div>
        </div>
        <div className="col-sm-5">
          <div className="form-floating">
            <select id={`etapa-proximo-responsavel-${etapaId}`} className="form-select" value={proxResp}
                    onChange={(ev) => setProxResp(ev.target.value)} disabled={!proxima.trim()}>
              <option value="">— sem responsável —</option>
              {usuarios.map((u) => (
                <option key={u.id} value={String(u.id)}>{u.nome}</option>
              ))}
            </select>
            <label htmlFor={`etapa-proximo-responsavel-${etapaId}`}>Responsável pela próxima</label>
          </div>
        </div>
        <div className="col-12 d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-sm btn-theme"
                  onClick={() => concluir('feita')} disabled={salvando}>
            {salvando && <span className="spinner-border spinner-border-sm me-1" />}
            Concluir
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary"
                  onClick={() => concluir('dispensada')} disabled={salvando}
                  title="Deixou de ser necessária — diferente de concluída">
            Dispensar
          </button>
          <button type="button" className="btn btn-sm btn-link ms-auto" onClick={() => aoFechar(false)}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
